import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, CheckIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { CartModal } from './CartModal';
import { TrainingPlanWarningModal } from './TrainingPlanWarningModal';
interface FAQItem {
  question: string;
  answer: string | JSX.Element;
}
interface PlanLevel {
  id: string;
  title: string;
  emoji: string;
  shortDescription: string;
  bulletPoints: string[];
  fullDescription: string;
  priceOneMonth: number;
  priceThreeMonths: number;
}
export const FAQSection: React.FC = () => {
  const [openItem, setOpenItem] = useState<number | null>(null);
  const {
    addItem,
    cartModalOpen,
    setCartModalOpen,
    lastAddedItem,
    trainingPlanWarningOpen,
    setTrainingPlanWarningOpen,
    existingTrainingPlanName
  } = useCart();
  const navigate = useNavigate();
  const [selectedDurations, setSelectedDurations] = useState<Record<string, '1' | '3'>>({
    beginner: '3',
    advanced: '3'
  });
  const [expandedPlans, setExpandedPlans] = useState<Record<string, boolean>>({});
  const toggleItem = (index: number) => {
    setOpenItem(openItem === index ? null : index);
  };
  const togglePlanDetails = (planId: string) => {
    setExpandedPlans(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };
  const handleDurationChange = (planId: string, duration: '1' | '3') => {
    setSelectedDurations(prev => ({
      ...prev,
      [planId]: duration
    }));
  };
  const handleAddToCart = (plan: PlanLevel) => {
    const duration = selectedDurations[plan.id];
    const price = duration === '1' ? plan.priceOneMonth : plan.priceThreeMonths;
    addItem({
      id: `training-${plan.id}-${duration}`,
      name: `Tréninkový plán - ${plan.title} (${duration === '1' ? '1 měsíc' : '3 měsíce'})`,
      price,
      type: 'training',
      details: `${plan.title}, ${duration === '1' ? '1 měsíc' : '3 měsíce'}`
    });
    // Immediately navigate to cart after adding item
    navigate('/cart');
    // Scroll to top will happen automatically in Cart component
  };
  const handleContinueShopping = () => {
    setCartModalOpen(false);
  };
  const handleGoToCart = () => {
    setCartModalOpen(false);
    navigate('/cart');
  };
  const faqItems: FAQItem[] = [{
    question: 'Jak získám přístup do aplikace a jak se přihlásím?',
    answer: 'Po úspěšné platbě ti do e-mailové schránky dorazí pozvánka k vytvoření účtu v aplikaci.\n\n1. Otevři e-mail s pozvánkou – najdeš v něm odkaz „Set up your account" (Vytvořit účet).\n\n2. Klikni na odkaz a vytvoř si vlastní heslo, případně doplň základní údaje o profilu.\n\n3. Stáhni si aplikaci „ABC Trainerize" podle odkazu v e-mailu –\n\n• z App Store (pro iPhone),\n\n• nebo z Google Play (pro Android).\n\n4. Přihlaš se do aplikace pomocí e-mailu a hesla, které jsi právě vytvořil(a).\n\nPo přihlášení budeš mít plný přístup ke svému tréninkovému plánu, videím a dalším materiálům.\n\nPřístup do aplikace je aktivní pouze pro klienty s uhrazeným plánem. Pokud ti pozvánka do e-mailu nedorazila do několika minut po platbě, zkontroluj složku Spam nebo Promo, anebo si přečti v sekci FAQ tuhle otázku "Kdy dostanu přístup do tréninkového plánu?"'
  }, {
    question: 'Proč nejsou k tréninkovým plánům rovnou i jídelníčky?',
    answer: <>
          Vím, že spousta lidí čeká k tréninkovému plánu i jídelníček. A upřímně
          – strava je obrovská část výsledků.
          {'\n\n'}
          Jídelníčky už jsou v procesu a časem budou součástí systému.
          {'\n\n'}
          Ale chtěl jsem udělat jednu věc:
          {'\n'}
          nejdřív vypustit tréninkové plány v TOP kvalitě, protože to je to, co
          většině lidí chybí nejvíc – systém, progres, správné cviky a jasná
          strategie.
          {'\n\n'}
          Zároveň ale nechci, aby někdo čekal na výsledky jen proto, že nemá
          „jídelníček na papíře".
          {'\n'}
          Proto jsem připravil jednoduchý výživový návod, který ti dá všechny
          zásadní informace, díky kterým si umíš postavit jídelníček sám – a
          bude fungovat.
          {'\n\n'}
          Když budeš dodržovat tyhle principy + můj tréninkový plán + Green Ways
          superpotraviny, tak máš všechno, co potřebuješ, aby ses posouval.
          {'\n'}
          Nemusíš jíst „kuře a rýži" každý den. Potřebuješ jen chápat systém.
          {'\n\n'}
          👉 Výživový návod najdeš tady:{' '}
          <a href="https://docs.google.com/document/d/13oZuzH3OlvEHzjy6DCsVnxrxqWeYBsiJMx29wwMbwIk/edit?tab=t.0" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-400 underline font-semibold transition-colors" data-id="element-421">
            Klikni zde
          </a>
        </>
  }, {
    question: 'Kdy dostanu přístup do tréninkového plánu?',
    answer: 'Přístup k aplikaci a tréninkovému plánu získáte podle toho, jaký datum začátku si zvolíte při nákupu. Pokud vyberete dnešní datum, přístup obdržíte okamžitě po zaplacení. Pokud si nastavíte jiný den, plán se zpřístupní až v den, který jste si zvolili. Maximální možný posun začátku je však 7 dní od nákupu – pokud zadáte datum pozdější, systém automaticky nastaví začátek na den nákupu. Pokud necháte pole s datem prázdné, plán začne ihned po provedení platby.'
  }, {
    question: 'Kde a jak dostanu přístup do treninkového plánu?',
    answer: 'Přístup dostanete přímo na email, pod kterým jste si objednali tréninkový plán, a na Váš email vám dojde pozvánka do aplikace Trainarize do které se už přihlásíte a poté budete mít dostupný tréninkový plán.'
  }, {
    question: 'V čem je můj online tréninkový plán jiný od konkurence?',
    answer: 'Tréninkový plán, je sestavený tak, aby ses přirozeně po uplynutí 3 měsíců naučil, jak cvičit správně a efektivně. 3 měsíční tréninkový plán, by tě měl dostat do takové fáze, aby si už nikdy nemusel platit za tréninkové plány online, nebo soukromého trenéra v posilovně, jelikož se naučíš jak cvičit přesně tak jak tvé tělo potřebuje, k požadovaným výsledkům. K samostatnosti u cvičení dospěješ díky, velmi detailním popisům jak cvičit, kolik dělat opakování, jak dlouho odpočívat mezi opakováními, důraz na tvrdé tréninky, ale na druhou stranu i důraz na dostatečnou regeneraci pro tělo. Při cvičení Vám také napomůžou video ukázky, pro docílení správné techniky.'
  }, {
    question: 'Proč si raději vybrat můj plán a nezaplatit si soukromého trenéra?',
    answer: 'Protože já mám přesně opačný záměr než většina trhu. Já nechci, abys na mně byl závislý. Nechci, abys několik měsíců, někdy i roků platil znovu. Nechci ti prodávat „motivaci". Já ti dávám přesný systém, který tě za 3 měsíce dovede do bodu, kdy budeš schopný cvičit sám a růst dál bez toho, aby sis kdy znovu musel platit plán nebo trenéra.\n\nMůj plán není chaos. Je to opak chaosu. Je to jasně daný tréninkový pattern, který se opakuje tak, aby ses v něm zlepšoval každý týden. Právě díky tomu začneš poprvé v životě chápat, že svaly nerostou z náhodných cviků, ale z progresu. Z toho, že se zlepšuješ v těch podobných pohybech, ve stejném systému, a přesně víš, co máš dělat.\n\nTohle je rozdíl mezi lidmi, kteří cvičí roky a pořád nevypadají, a lidmi, kteří během pár měsíců vystřelí. Jedna věc. Systém. Přesná strategie. A důsledné trackování, které tě donutí zlepšovat se, protože máš čísla před sebou a vidíš, co se děje.\n\nJá jsem si na tohle musel přijít sám. Několik let pokusů a omylů. Studoval jsem věci, které běžný člověk v posilovně nikdy řešit nebude. A právě proto jsem udělal tenhle plán - abys nemusel ztratit roky života tím, že budeš hádat, co funguje a co ne.\n\nTy u mě nedostaneš jen „cviky". Dostaneš know-how. Dostaneš postup. Dostaneš vysvětlení, proč přesně tohle děláš, jaký to má smysl, jak to ovlivní tvůj růst a jak tohle jednou budeš schopný postavit i sám. A pokud budeš konzistentní, tak po třech měsících nebudeš pouze jinak vypadat. Budeš mít v hlavě systém, který ti zůstane do konce života.'
  }, {
    question: 'Jak je to cenově v porovnání se soukromým trenérem?',
    answer: 'Někteří soukromý trenéři se nestydí si nechat zaplatit až 500kč na hodinu. Tudíž pokud si vezmeme, že by jsme chodili k soukromému trenérovi po dobu 3 měsiců, 5x za týden do posilovny na hodinu (obvykle se cvičí i déle) tak to vychází na neuvěřitelných 30 000kč za 3 měsíce!!, což je v porovnání s cenou mých tréninkových plánů nesrovnatelná cena (1799-1999kč) za 3 měsíce. Když si zákazník vezme v potaz to, že až uplynou 3 měsíce, tak trenéři Vás stále budou chtít trénovat, jelikož za tu dobu stále nemáte dostatek znalostí o cvičení. Také velkou roli může hrát to, že pro některé lidi to může být velmi nekomfortní cvičit s trénerem, který Vás má pořád pod dohledem. Další velmi zásadní rozdíl mezi mým tréninkovým plánem a plánem soukromých trenérů je, že se musíte přizpůsobit s časem v kolik hodin zrovna může, což může vést k demotivaci a výmluvám, takže kolikrát zákazník ani nejde kvůli tomu, že jeho trenér nemá čas, nebo Vám to zrovna nevyhovuje. Proto jsem udělal můj online tréninkový plán ve kterém se Vám ani jedna z těhle věcí nestane! Nebudete platit desítky tisíc za trenéra, můžete jít kdykoliv cvičit kdy máte zrovna volno a nemusíte se vázat na trenéra, a ta nejdůležitější věc je, že se naučíte po 3 měsících správně cvičit a chápat jak Vaše tělo funguje a co přesně dělat pro nejrychlejší růst svalů!'
  }, {
    question: 'Kdy uvidím výsledky?',
    answer: 'U každého je to jiné. Někdo cítí změnu za pár dní, někdo za pár týdnů. Důležitá je konzistence – jako to je se vším v životě. Tělo potřebuje čas.'
  }];
  const plans: PlanLevel[] = [{
    id: 'beginner',
    title: 'BHS SYSTÉM',
    emoji: '💪',
    shortDescription: '',
    bulletPoints: ['Videoukázky ke každému cviku (správná technika + tempo)', 'Split: 4 tréninky / 3 dny odpočinku', 'Přesně dané série, opakování a pauzy', 'Systém zapisování a sledování progresu', 'Jasně strukturovaný progresivní overload', 'Využití začátečnického potenciálu pro rychlé výsledky', 'Správná délka pauz mezi sériemi pro maximální růst', 'Plán postavený na základě desítek studií + letech zkušeností', 'Plán, který ti dá know-how na to jak správně cvičit', 'Každé 2 týdny 1:1 online check-up call pro kontrolu tvého progresu'],
    fullDescription: 'Celý plán je navržený tak, aby si co nejlépe využil tzv. začátečníký potenciál – tedy období, kdy tělo roste nejrychleji a výsledky jdou vidět téměř okamžitě. Zároveň ale dbáme na to, abys ses nepřetrénoval/a – svaly potřebují čas na regeneraci. A právě regeneraci plán zohledňuje, tak aby sis mohl/a budovat formu bezpečně a efektivně. Nemusíš se ničeho bát – všechno ti ukážeme krok po kroku. Stačí nabrat odvahu a konečně se rozhodnout se změnit jak fyzicky, tak psychicky. Možná právě v tomhle tréninkovém plánu, se skrývá klíč k sebevědomí a změně sebe, po které toužíš. Stačí se rozhodnout investovat do sebe, udělat první krok – jakmile uvidíš reálné výsledky pak už tě nic nezastaví. Stačí začít!',
    priceOneMonth: 899,
    priceThreeMonths: 1799
  }];
  return <section className="section section-dark relative py-20" id="faq" data-id="element-422">
      <CartModal isOpen={cartModalOpen} onClose={handleContinueShopping} onGoToCart={handleGoToCart} onContinueShopping={handleContinueShopping} productName={lastAddedItem || ''} data-id="element-423" />

      <TrainingPlanWarningModal isOpen={trainingPlanWarningOpen} onClose={() => setTrainingPlanWarningOpen(false)} existingPlanName={existingTrainingPlanName || ''} data-id="element-424" />

      <div className="container mx-auto px-4 section-content" data-id="element-425">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} viewport={{
        once: true
      }} className="max-w-4xl mx-auto mb-20" data-id="element-426">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 md:p-10 luxury-card border border-green-500/30 shadow-[0_10px_40px_-5px_rgba(34,197,94,0.2)] relative overflow-hidden" data-id="element-427">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-428"></div>
            <div className="absolute -right-20 -top-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-429"></div>
            <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-green-500/5 rounded-full filter blur-3xl" data-id="element-430"></div>

            <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center relative" data-id="element-431">
              <span className="relative inline-block" data-id="element-432">
                <span className="relative z-10" data-id="element-433">
                  FAQ - <span className="text-green-500" data-id="element-434">Časté otázky</span>
                </span>
                <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent" data-id="element-435"></span>
              </span>
            </h2>

            <div className="space-y-4" data-id="element-436">
              {faqItems.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: index * 0.1
            }} viewport={{
              once: true
            }} className="bg-black/30 rounded-lg border border-green-500/20 overflow-hidden hover:border-green-500/40 transition-all duration-300" data-id="element-437">
                  <button onClick={() => toggleItem(index)} className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-green-500/5 transition-colors duration-300" data-id="element-438">
                    <span className="text-base md:text-lg font-medium text-white pr-4" data-id="element-439">
                      {item.question}
                    </span>
                    <motion.div animate={{
                  rotate: openItem === index ? 180 : 0
                }} transition={{
                  duration: 0.3
                }} className="flex-shrink-0" data-id="element-440">
                      <ChevronDownIcon className="w-5 h-5 text-green-500" data-id="element-441" />
                    </motion.div>
                  </button>

                  <AnimatePresence data-id="element-442">
                    {openItem === index && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3,
                  ease: 'easeInOut'
                }} className="overflow-hidden" data-id="element-443">
                        <div className="px-6 pb-4 pt-2" data-id="element-444">
                          <div className="text-sm md:text-base text-gray-300 leading-relaxed whitespace-pre-line" data-id="element-445">
                            {item.answer}
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>)}
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} viewport={{
        once: true
      }} className="mb-6 text-center" data-id="element-446"></motion.div>

        <div className="grid grid-cols-1 gap-6 training-plans-cards max-w-lg mx-auto" data-id="element-447">
          {plans.map((plan, index) => <motion.div key={plan.id} className="premium-card flex flex-col h-full bg-gray-900/80 border border-gray-800" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.2
        }} viewport={{
          once: true
        }} whileHover={{
          y: -5,
          boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.3)',
          borderColor: 'rgba(34, 197, 94, 0.3)'
        }} data-id="element-448">
              {/* Header with title */}
              <div className="p-4 md:p-5 border-b border-gray-800 relative" data-id="element-449">
                <div className="flex items-center justify-between" data-id="element-450">
                  <h3 className="text-lg md:text-xl font-black text-green-500 flex items-center" data-id="element-451">
                    {plan.title}
                  </h3>
                  <AnimatePresence data-id="element-452">
                    {plan.id === 'beginner' && selectedDurations[plan.id] === '3' && <motion.div initial={{
                  opacity: 0,
                  scale: 0.8
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.8
                }} transition={{
                  duration: 0.2
                }} className="relative ml-3" data-id="element-453">
                          <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-500 rounded-full blur-sm opacity-60 animate-pulse" data-id="element-454"></div>
                          <div className="relative bg-gradient-to-r from-green-600 to-green-500 text-white text-[9px] md:text-[11px] font-black px-2.5 md:px-3 py-1 md:py-1.5 rounded-full shadow-lg border border-white/30" data-id="element-455">
                            <span className="relative z-10 tracking-wider whitespace-nowrap" data-id="element-456">
                              NEJPRODÁVANĚJŠÍ
                            </span>
                          </div>
                        </motion.div>}
                  </AnimatePresence>
                </div>
              </div>
              {/* Content */}
              <div className="p-4 md:p-5 flex-1 flex flex-col" data-id="element-457">
                <p className="text-gray-300 text-xs md:text-sm mb-3" data-id="element-458">
                  {plan.shortDescription}
                </p>
                <div className="flex-1 flex flex-col" data-id="element-459">
                  <ul className="space-y-3 mb-4 flex-1" data-id="element-460">
                    {plan.bulletPoints.map((point, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-461">
                        <div className="flex-shrink-0 mt-0.5" data-id="element-462">
                          <CheckIcon className="w-5 h-5 text-green-500" strokeWidth={2.5} data-id="element-463" />
                        </div>
                        <span className="text-gray-200 text-sm md:text-base font-medium leading-relaxed" data-id="element-464">
                          {point}
                        </span>
                      </li>)}
                  </ul>
                  <motion.button onClick={() => togglePlanDetails(plan.id)} className="flex items-center text-green-500 hover:text-green-400 transition-colors text-xs font-bold mb-4" whileHover={{
                x: 5
              }} transition={{
                duration: 0.2
              }} data-id="element-465">
                    {expandedPlans[plan.id] ? <>
                        <span data-id="element-466">MÉNĚ INFORMACÍ</span>
                        <ChevronUpIcon className="w-4 h-4 ml-1" data-id="element-467" />
                      </> : <>
                        <span data-id="element-468">VÍCE INFORMACÍ</span>
                        <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-469" />
                      </>}
                  </motion.button>
                  <motion.div initial={false} animate={{
                height: expandedPlans[plan.id] ? 'auto' : 0,
                opacity: expandedPlans[plan.id] ? 1 : 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-470">
                    {expandedPlans[plan.id] && <div className="mt-2 text-gray-300 bg-gray-800 p-4 rounded-lg text-xs mb-4" data-id="element-471">
                        <p className="whitespace-pre-line" data-id="element-472">
                          {plan.fullDescription}
                        </p>
                      </div>}
                  </motion.div>
                  {/* Duration selection tabs */}
                  <div className="flex justify-center mb-4 bg-gray-800 rounded-md overflow-hidden" data-id="element-473">
                    <button onClick={() => handleDurationChange(plan.id, '1')} className={`flex-1 py-3 text-center font-bold transition-colors text-xs ${selectedDurations[plan.id] === '1' ? 'bg-gray-700 text-white' : 'bg-transparent text-gray-400 hover:bg-gray-700/50'}`} data-id="element-474">
                      1 měsíc
                    </button>
                    <button onClick={() => handleDurationChange(plan.id, '3')} className={`flex-1 py-3 text-center font-bold transition-colors text-xs ${selectedDurations[plan.id] === '3' ? 'bg-green-600 text-white' : 'bg-transparent text-gray-400 hover:bg-gray-700/50'}`} data-id="element-475">
                      3 měsíce
                    </button>
                  </div>
                  {/* Selected price display */}
                  <div className="text-center mb-4 md:mb-6" data-id="element-476">
                    <div className="text-2xl md:text-4xl font-black text-green-500 mb-2 drop-shadow-[0_0_10px_rgba(34,197,94,0.3)]" data-id="element-477">
                      {selectedDurations[plan.id] === '1' ? `${plan.priceOneMonth} Kč` : `${plan.priceThreeMonths} Kč`}
                    </div>
                    {selectedDurations[plan.id] === '3' && <div className="relative inline-block bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/20 rounded-lg px-4 py-2 mb-2 backdrop-blur-sm" data-id="element-478">
                        <div className="text-xs md:text-sm text-gray-300 flex items-center justify-center gap-2" data-id="element-479">
                          <span className="text-gray-500 line-through decoration-red-500/40 decoration-1" data-id="element-480">
                            {plan.priceOneMonth * 3} Kč
                          </span>
                          <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300 uppercase tracking-wide" data-id="element-481">
                            Ušetříš{' '}
                            {plan.priceOneMonth * 3 - plan.priceThreeMonths} Kč
                          </span>
                        </div>
                      </div>}
                    <div className="flex items-center justify-center gap-2 text-sm md:text-base mt-2" data-id="element-482">
                      <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-emerald-300 to-green-400 tracking-wide drop-shadow-[0_0_8px_rgba(34,197,94,0.4)]" data-id="element-483">
                        Jednou zaplatíš a už nikdy nebudeš muset platit znovu!
                      </span>
                    </div>
                  </div>
                  {/* Add to cart button */}
                  <button onClick={() => handleAddToCart(plan)} className="bg-green-600 text-white py-3 md:py-4 rounded-md font-bold flex items-center justify-center hover:bg-green-700 transition-colors text-xs md:text-base" data-id="element-484">
                    <CheckIcon className="w-4 h-4 md:w-5 md:h-5 mr-2" data-id="element-485" />
                    PŘIDAT DO KOŠÍKU
                  </button>
                </div>
              </div>
            </motion.div>)}
        </div>
        <div className="mt-8 md:mt-12" data-id="element-486">
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-6 md:mb-8" data-id="element-487">
            <div className="flex items-center" data-id="element-488">
              <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-2" data-id="element-489" />
              <span className="text-gray-300 text-xs md:text-base" data-id="element-490">
                Přehledná aplikace
              </span>
            </div>
            <div className="flex items-center" data-id="element-491">
              <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-2" data-id="element-492" />
              <span className="text-gray-300 text-xs md:text-base" data-id="element-493">
                Jednorázová platba
              </span>
            </div>
            <div className="flex items-center" data-id="element-494">
              <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-2" data-id="element-495" />
              <span className="text-gray-300 text-xs md:text-base" data-id="element-496">
                Získáš okamžitý přístup do emailu
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="section-divider divider-slant-up" data-id="element-497"></div>
    </section>;
};